import { useEffect, useState } from "react";
import AdminHeader from "../../component/AdminHeader";
import ApiRequest from "../../helper/ApiRequest";
import { ApiEndpoints } from "../../helper/ApiEndpoints";
import { toast } from "react-toastify";
import AdditionalSchemas, {
  normalizeAdditionalSchemas,
} from "../Seo/AdditionalSchemas";

const schemaViewEndpoint = ApiEndpoints.globalSeo.view;
const schemaUpdateEndpoint = ApiEndpoints.globalSeo.update;

export default function SchemaManagement() {
  const [schemaData, setSchemaData] = useState({
    review_schema: "",
    organisation_schema: "",
    twitter_site: "",
    twitter_card: "",
    twitter_creator: "",
    og_site_name: "",
    additional_schemas: [],
  });
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const pagename = {
    name: "Global SEO",
    user: "Admin",
    innerpage: "Global SEO",
  };

  const fetchSchema = async () => {
    try {
      const response = await ApiRequest("GET", schemaViewEndpoint);
      setSchemaData((current) => ({
        ...current,
        ...(response?.schema || {}),
        additional_schemas: normalizeAdditionalSchemas(response?.schema?.additional_schemas),
      }));
    } catch (error) {
      console.log(error);
      toast.error("Unable to load schema.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSchema();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors([]);

    const validationErrors = [
      { label: "Review Schema", schema: schemaData.review_schema },
      { label: "Organization Schema", schema: schemaData.organisation_schema },
      ...normalizeAdditionalSchemas(schemaData.additional_schemas).map((item) => ({
        label: item.label || "Additional Schema",
        schema: item.schema,
      })),
    ].reduce((messages, item) => {
      if (/<\/?\s*script\b/i.test(item.schema)) {
        messages.push(`${item.label} should contain only JSON-LD content. Please remove the <script> tag.`);
      } else if (item.schema?.trim()) {
        try {
          JSON.parse(item.schema);
        } catch (error) {
          messages.push(`${item.label} must contain valid JSON-LD.`);
        }
      }
      return messages;
    }, []);

    if (validationErrors.length) {
      setErrors(validationErrors);
      toast.error("Please correct the Global SEO schema fields.");
      return;
    }

    try {
      setSaving(true);
      const response = await ApiRequest("POST", schemaUpdateEndpoint, schemaData);
      toast.success(response.message);
    } catch (error) {
      const apiErrors = error?.response?.data?.errors || [];
      setErrors(apiErrors);
      toast.error(error?.response?.data?.message || "Unable to update schema.");
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setSchemaData((current) => ({ ...current, [name]: value }));
  };

  return (
    <>
      <AdminHeader pagename={pagename} />

      <div className="add-content-page">
        <form onSubmit={handleSubmit}>
          <div className="add-content-wrap">
            <div className="row">
              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Twitter Site</label>
                  </div>
                  <input
                    type="text"
                    name="twitter_site"
                    value={schemaData.twitter_site}
                    onChange={handleChange}
                    className="form-control"
                    disabled={loading || saving}
                  />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Twitter Card</label>
                  </div>
                  <input
                    type="text"
                    name="twitter_card"
                    value={schemaData.twitter_card}
                    onChange={handleChange}
                    className="form-control"
                    disabled={loading || saving}
                  />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Twitter Creator (Twitter Account)</label>
                  </div>
                  <input
                    type="text"
                    name="twitter_creator"
                    value={schemaData.twitter_creator}
                    onChange={handleChange}
                    className="form-control"
                    disabled={loading || saving}
                  />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>OG Site Name</label>
                  </div>
                  <input
                    type="text"
                    name="og_site_name"
                    value={schemaData.og_site_name}
                    onChange={handleChange}
                    className="form-control"
                    disabled={loading || saving}
                  />
                </div>
              </div>

              <div className="col-lg-12">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Review Schema</label>
                  </div>
                  <textarea
                    name="review_schema"
                    value={schemaData.review_schema}
                    onChange={handleChange}
                    className="form-control"
                    rows="14"
                    disabled={loading || saving}
                    style={{ height: "auto", fontFamily: "monospace", whiteSpace: "pre", overflow: "auto" }}
                  />
                </div>
              </div>

              <div className="col-lg-12">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Organization Schema</label>
                  </div>
                  <textarea
                    name="organisation_schema"
                    value={schemaData.organisation_schema}
                    onChange={handleChange}
                    className="form-control"
                    rows="14"
                    disabled={loading || saving}
                    style={{ height: "auto", fontFamily: "monospace", whiteSpace: "pre", overflow: "auto" }}
                  />
                </div>
              </div>

              {errors.length > 0 && (
                <div className="col-lg-12">
                  <div className="alert alert-danger">
                    {errors.map((error) => (
                      <div key={error}>{error}</div>
                    ))}
                  </div>
                </div>
              )}

              <div className="col-lg-12">
                <AdditionalSchemas
                  value={schemaData.additional_schemas}
                  onChange={(additional_schemas) =>
                    setSchemaData((current) => ({ ...current, additional_schemas }))
                  }
                />
              </div>

              <div className="col-lg-12">
                <button className="default-btn" type="submit" disabled={loading || saving}>
                  {saving ? "Saving..." : "Save"}<span></span>
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </>
  );
}
